'use client';
import React, { useState } from 'react';
import { Truck, Wrench, User, Navigation, Maximize2, Layers, RefreshCw, Route } from 'lucide-react';
import type { Asset } from './LiveTrackingView';



interface MapAreaProps {
  assets: Asset[];
  selectedAssetId: string | null;
  onSelectAsset: (id: string) => void;
}

const statusColor: Record<string, string> = {
  'en-route': '#38BDF8',
  'on-site': '#A78BFA',
  'returning': '#F59E0B',
  'available': '#34D399',
  'offline': '#71717A',
  'maintenance': '#F97316',
  'dispatched': '#60A5FA',
};

// Simulated map positions (normalized 0-100 for the container)
const assetPositions: Record<string, { x: number; y: number }> = {
  'asset-v01': { x: 62, y: 38 },
  'asset-v02': { x: 34, y: 55 },
  'asset-v03': { x: 78, y: 22 },
  'asset-v04': { x: 18, y: 72 },
  'asset-v05': { x: 71, y: 28 },
  'asset-v06': { x: 79, y: 18 },
  'asset-e01': { x: 35, y: 56 },
  'asset-e02': { x: 12, y: 82 },
  'asset-p01': { x: 13, y: 81 },
  'asset-p02': { x: 63, y: 37 },
};

const typeIcon = {
  vehicle: Truck,
  equipment: Wrench,
  person: User,
};

export default function MapArea({ assets, selectedAssetId, onSelectAsset }: MapAreaProps) {
  const [showHistory, setShowHistory] = useState(true);

  const selectedAsset = assets.find((a) => a.id === selectedAssetId) ?? null;

  return (
    <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden relative min-w-0">
      {/* Map controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
        <button className="w-8 h-8 bg-zinc-800 border border-zinc-700 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-all">
          <Maximize2 size={13} />
        </button>
        <button className="w-8 h-8 bg-zinc-800 border border-zinc-700 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-all">
          <Layers size={13} />
        </button>
        <button className="w-8 h-8 bg-zinc-800 border border-zinc-700 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-all">
          <RefreshCw size={13} />
        </button>
        {/* Toggle travel history */}
        <button
          onClick={() => setShowHistory((v) => !v)}
          title={showHistory ? 'Hide travel history' : 'Show travel history'}
          className={`w-8 h-8 border rounded-lg flex items-center justify-center transition-all ${showHistory ? 'bg-amber-500/20 border-amber-500/40 text-amber-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'}`}
        >
          <Route size={13} />
        </button>
      </div>

      {/* Map legend */}
      <div className="absolute bottom-3 left-3 z-10 bg-zinc-900/90 border border-zinc-700 rounded-lg px-3 py-2 backdrop-blur-sm">
        <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Status</p>
        <div className="space-y-1">
          {[
            { color: '#38BDF8', label: 'En Route' },
            { color: '#A78BFA', label: 'On Site' },
            { color: '#F59E0B', label: 'Returning' },
            { color: '#34D399', label: 'Available' },
            { color: '#71717A', label: 'Offline' },
          ].map((item) => (
            <div key={`legend-${item.label}`} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-[10px] text-zinc-400">{item.label}</span>
            </div>
          ))}
        </div>
        {showHistory && (
          <div className="mt-2 pt-2 border-t border-zinc-700/50">
            <div className="flex items-center gap-1.5">
              <svg width="16" height="6"><line x1="0" y1="3" x2="16" y2="3" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="3,2" /></svg>
              <span className="text-[10px] text-zinc-400">Travel history</span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0 bg-amber-400/60 border border-amber-400" />
              <span className="text-[10px] text-zinc-400">Timestamp stop</span>
            </div>
          </div>
        )}
      </div>

      {/* Selected asset distance badge */}
      {selectedAsset && selectedAsset.distanceTodayMiles !== undefined && (
        <div className="absolute top-3 left-3 z-10 bg-zinc-900/95 border border-amber-500/30 rounded-lg px-3 py-2 backdrop-blur-sm max-w-[200px]">
          <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
            <Route size={10} /> {selectedAsset.unitNumber}
          </p>
          <div className="flex items-center gap-3">
            <div>
              <p className="text-base font-bold text-zinc-100 font-mono leading-none">{selectedAsset.distanceTodayMiles}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">miles today</p>
            </div>
            {selectedAsset.travelHistory && selectedAsset.travelHistory.length > 1 && (
              <div className="border-l border-zinc-700 pl-3">
                <p className="text-[11px] font-mono text-zinc-300 leading-none">{selectedAsset.travelHistory[0].timestamp}</p>
                <p className="text-[9px] text-zinc-600 mt-0.5">first ping</p>
                <p className="text-[11px] font-mono text-zinc-300 leading-none mt-1">{selectedAsset.travelHistory[selectedAsset.travelHistory.length - 1].timestamp}</p>
                <p className="text-[9px] text-zinc-600 mt-0.5">last ping</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Simulated map background */}
      <div className="absolute inset-0 bg-zinc-950">
        {/* Grid lines simulating map */}
        <svg width="100%" height="100%" className="opacity-20">
          <defs>
            <pattern id="map-grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgb(63 63 70)" strokeWidth="0.5" />
            </pattern>
            <pattern id="map-grid-major" width="180" height="180" patternUnits="userSpaceOnUse">
              <path d="M 180 0 L 0 0 0 180" fill="none" stroke="rgb(82 82 91)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#map-grid)" />
          <rect width="100%" height="100%" fill="url(#map-grid-major)" />
          {/* Simulated roads */}
          <line x1="0" y1="45%" x2="100%" y2="48%" stroke="rgb(63 63 70)" strokeWidth="3" />
          <line x1="30%" y1="0" x2="35%" y2="100%" stroke="rgb(63 63 70)" strokeWidth="2" />
          <line x1="60%" y1="0" x2="65%" y2="100%" stroke="rgb(63 63 70)" strokeWidth="2.5" />
          <line x1="0" y1="70%" x2="100%" y2="72%" stroke="rgb(63 63 70)" strokeWidth="1.5" />
          <path d="M 20% 0 Q 45% 30% 80% 60% T 100% 90%" fill="none" stroke="rgb(63 63 70)" strokeWidth="2" />
          {/* Location labels */}
          <text x="72%" y="15%" fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Midland, TX</text>
          <text x="25%" y="60%" fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Odessa, TX</text>
          <text x="8%" y="88%" fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Loving County</text>
          <text x="50%" y="35%" fill="rgb(82 82 91)" fontSize="9" fontFamily="JetBrains Mono">Permian Basin</text>
        </svg>
      </div>

      {/* Travel history overlay — SVG layer */}
      {showHistory && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-10"
          style={{ overflow: 'visible' }}
        >
          {assets.map((asset) => {
            const history = asset.travelHistory;
            if (!history || history.length < 2) return null;
            const isSelected = selectedAssetId === asset.id;
            const color = statusColor[asset.status] ?? '#71717A';
            const opacity = isSelected ? 1 : 0.35;

            // Build polyline points string
            const points = history.map((p) => `${p.x}%,${p.y}%`).join(' ');

            return (
              <g key={`history-${asset.id}`} opacity={opacity}>
                {/* Route trail */}
                <polyline
                  points={points}
                  fill="none"
                  stroke={isSelected ? '#F59E0B' : color}
                  strokeWidth={isSelected ? 2 : 1.5}
                  strokeDasharray={isSelected ? '5,3' : '3,3'}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Timestamp markers — show all for selected, only first/last for others */}
                {history.map((point, idx) => {
                  const showMarker = isSelected || idx === 0 || idx === history.length - 1;
                  if (!showMarker) return null;
                  const isFirst = idx === 0;
                  const isLast = idx === history.length - 1;
                  return (
                    <g key={`marker-${asset.id}-${idx}`}>
                      {/* Dot */}
                      <circle
                        cx={`${point.x}%`}
                        cy={`${point.y}%`}
                        r={isSelected ? (isFirst || isLast ? 5 : 3.5) : 3}
                        fill={isFirst ? '#34D399' : isLast ? '#F59E0B' : '#F59E0B'}
                        fillOpacity={isSelected ? 0.9 : 0.6}
                        stroke={isSelected ? '#1C1C1E' : 'none'}
                        strokeWidth={1.5}
                      />
                      {/* Timestamp label — only for selected asset */}
                      {isSelected && (
                        <g>
                          <rect
                            x={`calc(${point.x}% + 7px)`}
                            y={`calc(${point.y}% - 9px)`}
                            width="38"
                            height="14"
                            rx="3"
                            fill="#18181B"
                            fillOpacity="0.92"
                            stroke="#3F3F46"
                            strokeWidth="0.5"
                          />
                          <text
                            x={`calc(${point.x}% + 26px)`}
                            y={`calc(${point.y}% + 1px)`}
                            textAnchor="middle"
                            fill="#D4D4D8"
                            fontSize="8"
                            fontFamily="JetBrains Mono, monospace"
                          >
                            {point.timestamp}
                          </text>
                          {/* Speed label for selected non-zero speed points */}
                          {isSelected && point.speed > 0 && (
                            <text
                              x={`calc(${point.x}% + 26px)`}
                              y={`calc(${point.y}% + 18px)`}
                              textAnchor="middle"
                              fill="#71717A"
                              fontSize="7"
                              fontFamily="JetBrains Mono, monospace"
                            >
                              {point.speed}mph
                            </text>
                          )}
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      )}

      {/* Asset pins */}
      {assets.map((asset) => {
        const pos = assetPositions[asset.id];
        if (!pos) return null;
        const PinIcon = typeIcon[asset.type];
        const color = statusColor[asset.status] ?? '#71717A';
        const isSelected = selectedAssetId === asset.id;
        const hasAlert = asset.alerts.length > 0;
        return (
          <button
            key={asset.id}
            onClick={() => onSelectAsset(asset.id)}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20 group"
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
          >
            {/* Pulse ring for selected or alerted */}
            {(isSelected || hasAlert) && (
              <span
                className="absolute inset-0 rounded-full animate-ping opacity-60"
                style={{ backgroundColor: hasAlert ? '#EF4444' : color, transform: 'scale(1.8)' }}
              />
            )}
            <div
              className={`relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-150 shadow-lg ${isSelected ? 'scale-125' : 'hover:scale-110'}`}
              style={{
                backgroundColor: `${color}22`,
                borderColor: hasAlert ? '#EF4444' : color,
                boxShadow: isSelected ? `0 0 12px ${color}66` : undefined,
              }}
            >
              <PinIcon size={13} style={{ color }} />
              {asset.speed > 0 && (
                <div
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <Navigation size={7} className="text-zinc-950" />
                </div>
              )}
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-200 font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl">
              {asset.unitNumber}
              {asset.driver && ` · ${asset.driver.split(' ')[0]}`}
              {asset.speed > 0 && ` · ${asset.speed}mph`}
              {asset.distanceTodayMiles !== undefined && ` · ${asset.distanceTodayMiles}mi today`}
            </div>
          </button>
        );
      })}

      {/* Map attribution */}
      <div className="absolute bottom-3 right-3 text-[9px] text-zinc-700 font-mono">
        Simulated Map View · GPS data live
      </div>
    </div>
  );
}